import { useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import { getFromToValues } from '@/features/data-hub/lib';
import type { ExportSettingsType } from '@/features/data-hub/types/context.types';

import {
  buildExportApiRequestString,
  buildExportCurlRequestString
} from '@/features/data-hub/lib/ExportRequestService';
import { useLatestApiKey } from './useLatestApiKey';

type UseExportApiStringsParams = {
  symbols: SymbolKey[];
  exportSettings: ExportSettingsType;
  userTimezone: string;
  gxURL: string;
};

const useExportApiStrings = ({
  symbols,
  exportSettings,
  userTimezone,
  gxURL
}: UseExportApiStringsParams) => {
  const apiKey = useLatestApiKey();
  const { fromValue, toValue } = getFromToValues(exportSettings);

  const baseParams = useMemo(
    () => ({
      gxURL,
      symbols,
      fromValue,
      toValue: toValue || null,
      includeMetadata: exportSettings.includeMetadata,
      userTimezone
    }),
    [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, gxURL]
  );

  const exportApiRequestString = useMemo(
    () => buildExportApiRequestString({ ...baseParams, apiKey }),
    [baseParams, apiKey]
  );

  const exportCurlRequestString = useMemo(
    () => buildExportCurlRequestString({ ...baseParams, apiKey: apiKey ?? '' }),
    [baseParams, apiKey]
  );

  return { exportApiRequestString, exportCurlRequestString };
};

export default useExportApiStrings;
